import React, { forwardRef, useMemo, useRef } from 'react';
import { useSelector } from 'react-redux';
import Button from '../webapp/core/Button';
import { resolveProposalAssets } from '../../data/proposalAssetsData';
import { calculateBackdrop } from '../../utils/proposalBackdrop';
import { getProposalTemplates } from '../../selectors/selectors';
import { getSectionDataAttribute } from '../../utils/proposalSectionIds';
import { getPlaceholder } from '../../constants/proposalPlaceholders';
import FaceEffectDemo from './FaceEffectDemo';
import ShareDemo from './ShareDemo';
import EmailDemo from './EmailDemo';
import SMSDemo from './SMSDemo';
import LiveGalleryDemo from './LiveGalleryDemo';
import TemplatesOverlaysDisplay from './TemplatesOverlaysDisplay';
import BoothDemo from './BoothDemo';
import VideoDemo from './VideoDemo';
import PrintDemo from './PrintDemo';
import BackdropDemo from './BackdropDemo';
import ProposalCarousel from './ProposalCarousel';

const inputValidators = require('../../utils/inputValidators');

/**
 * Universal ProposalSection Component
 *
 * @param {string} sectionId - Internal section ID (e.g., "liveGallery", "photos", "masks")
 * @param {object} sectionData - Section data from proposalConfig
 */
const ProposalSection = forwardRef(({ sectionId, sectionData }, ref) => {
  const event = useSelector((state) => state.proposalData?.data);
  const proposalTemplates = useSelector(getProposalTemplates);
  const hasPhotoTemplates = proposalTemplates.length > 0;
  const previewContainer = useRef(null);

  if (!sectionData || !sectionData.enabled) {
    return null;
  }

  const isPricing = sectionId === 'pricing';
  const isInterfaceAndLed = sectionId === 'interfaceAndLed';
  const isPhotos = sectionId === 'photos';
  const isGreenScreen = sectionId === 'greenScreen';
  const isBackdropSection = sectionId === 'backdropSection';
  const isMasks = sectionId === 'masks';
  const isGifs = sectionId === 'gifs';
  const isGlam = sectionId === 'glam';
  const isPoseTips = sectionId === 'poseTips';
  const isShareGeneral = sectionId === 'sharing-general';
  const isEmail = sectionId === 'email';
  const isSMS = sectionId === 'sms';
  const isLiveGallery = sectionId === 'liveGallery';
  const isTemplatesOverlays = sectionId === 'templatesOverlays';
  const isCallToAction = sectionId === 'callToAction';

  const greenScreenOverlayIds = useMemo(() => {
    const greenScreens = event?.captureConfig?.greenScreens;
    if (!greenScreens || !Array.isArray(greenScreens) || greenScreens.length === 0) {
      return [];
    }
    return greenScreens
      .filter((gs) => gs && gs.overlays && gs.overlays.overlay)
      .map((greenScreen) => greenScreen.overlays.overlay[0])
      .filter(Boolean);
  }, [event]);

  const heading = isPricing
    ? sectionData.packageTitle
    : sectionData.headingText || getPlaceholder(sectionId, 'heading');
  const body = isPricing ? sectionData.packageDescription : sectionData.bodyText || getPlaceholder(sectionId, 'body');

  const backdrop = useMemo(() => {
    if (
      !isPhotos &&
      !isGreenScreen &&
      !isBackdropSection &&
      !isMasks &&
      !isEmail &&
      !isGifs &&
      !isGlam &&
      !isLiveGallery
    ) {
      return null;
    }
    return calculateBackdrop(event, sectionId);
  }, [isPhotos, isGreenScreen, isBackdropSection, isMasks, isEmail, isGifs, isGlam, isLiveGallery, event, sectionId]);

  const resolvedAssets = useMemo(() => {
    return resolveProposalAssets(sectionData.imageUrls || [], sectionData.systemAssetId);
  }, [sectionData.imageUrls, sectionData.systemAssetId]);

  const assetSrc = resolvedAssets[0] || '';
  const isVideo = assetSrc && assetSrc.match(/\.(mp4|webm|ogg|m4v)$/i);

  // Component-based sections: override component with custom images (ignore systemAssetId)
  const isComponentSection =
    isInterfaceAndLed ||
    isBackdropSection ||
    isPoseTips ||
    isMasks ||
    isShareGeneral ||
    isEmail ||
    isSMS ||
    isLiveGallery;

  const previewContent = useMemo(() => {
    // For component-based sections: show carousel if 2+ custom images (override component)
    // Note: We ignore systemAssetId for these sections - they should only show component or custom images
    if (isComponentSection && sectionData.imageUrls?.length > 1) {
      // Resolve without systemAssetId for component sections
      const customAssets = resolveProposalAssets(sectionData.imageUrls, null);
      return <ProposalCarousel images={customAssets} heading={heading} />;
    }

    // Interface & LED: show component only if no custom images
    if (isInterfaceAndLed && !sectionData.imageUrls?.length) {
      const boothType = event.eventType === 'dslr' ? 'guac' : 'salsa';
      const startScreenUrl =
        event.startScreenUrl || event.brandingSettings?.startScreenUrl || '/static/booth/default-start.jpg';
      return (
        <BoothDemo
          boothType={boothType}
          ledRing={event.ledPatternModeEnabled ? event.ledBrandingAnimation : false}
          ledColor={!event.ledPatternModeEnabled ? event.ledBrandingColor : false}
          startScreenUrl={startScreenUrl}
        />
      );
    }

    // Photos: show carousel with default + custom images when imageUrls exist
    if (isPhotos && sectionData.imageUrls?.length > 0) {
      return <ProposalCarousel images={resolvedAssets} heading={heading} />;
    }

    // Photos: show component only if no custom images
    if (isPhotos && !sectionData.imageUrls?.length) {
      if (hasPhotoTemplates) {
        return (
          <PrintDemo
            ref={previewContainer}
            fonts={[...(event.printTemplateFonts || []), ...(event.overlaysFonts || [])]}
            backdrop={backdrop}
            greenScreenOverlayIds={greenScreenOverlayIds}
          />
        );
      }

      return <VideoDemo backdrop={backdrop} image={assetSrc} />;
    }

    if (isGreenScreen && !sectionData.imageUrls?.length) {
      return <VideoDemo backdrop={backdrop} greenScreenOverlayIds={greenScreenOverlayIds} image={assetSrc} />;
    }

    if (isBackdropSection && !sectionData.imageUrls?.length) {
      const boothType = event.eventType === 'dslr' ? 'guac' : 'salsa';
      return <BackdropDemo backdrop={backdrop} boothType={boothType} />;
    }

    if (isMasks && !sectionData.imageUrls?.length) {
      return <FaceEffectDemo backdrop={backdrop} />;
    }

    // GIFs: show VideoDemo with backdrop and green screen when no custom images
    if (isGifs && !sectionData.imageUrls?.length) {
      return (
        <VideoDemo backdrop={backdrop} gif={true} greenScreenOverlayIds={greenScreenOverlayIds} image={assetSrc} />
      );
    }

    // Glam: show FaceEffectDemo with backdrop when no custom images
    if (isGlam && !sectionData.imageUrls?.length) {
      return <VideoDemo backdrop={backdrop} greenScreenOverlayIds={greenScreenOverlayIds} image={assetSrc} />;
    }

    // Pose Tips: show BoothDemo with pose tips animation when no custom images
    if (isPoseTips && !sectionData.imageUrls?.length) {
      const boothType = event.eventType === 'dslr' ? 'guac' : 'salsa';
      return (
        <BoothDemo
          boothType={boothType}
          ledRing={event.ledPatternModeEnabled ? event.ledBrandingAnimation : false}
          ledColor={!event.ledPatternModeEnabled ? event.ledBrandingColor : false}
          poseTips={true}
        />
      );
    }

    // Sharing General: show component only if no custom images
    if (isShareGeneral && !sectionData.imageUrls?.length) {
      return (
        <ShareDemo
          email={['both', 'email'].includes(event.enabledSharingMethods)}
          text={['both', 'sms'].includes(event.enabledSharingMethods)}
          airDrop={event.airdropEnabled}
          survey={event.surveyEnabled}
          qr={event.qrCodeEnabled}
          print={event.airprintEnabled}
        />
      );
    }

    // Email: show component only if no custom images
    if (isEmail && !sectionData.imageUrls?.length) {
      return <EmailDemo backdrop={backdrop} template={event.emailTemplate} />;
    }

    // SMS: show component only if no custom images
    if (isSMS && !sectionData.imageUrls?.length) {
      const subdomain = event.liveGallery?.useBrandingSettings
        ? event.brandingSettings?.gallerySubdomain
        : event.liveGallery?.subdomain;
      return <SMSDemo shareText={event.shareText} subdomain={subdomain} />;
    }

    // Live Gallery: show component only if no custom images
    if (isLiveGallery && !sectionData.imageUrls?.length) {
      return (
        <LiveGalleryDemo
          title={event.title}
          subtitle={event.liveGalleryDemo?.subtitle}
          buttonFlag={event.liveGalleryDemo?.buttonFlag}
          buttonText={event.liveGalleryDemo?.buttonText}
          logo={event.liveGalleryDemo?.logoUrl}
          backdrop={backdrop}
          background={event.liveGalleryDemo?.backgroundUrl}
          accentColor={event.liveGalleryDemo?.micrositeAccentColor}
          theme={event.liveGalleryDemo?.theme}
        />
      );
    }

    // Component sections with single custom image: show that image (override component)
    if (isComponentSection && sectionData.imageUrls?.length === 1) {
      const customAssets = resolveProposalAssets(sectionData.imageUrls, null);
      const customSrc = customAssets[0];
      if (customSrc) {
        const isCustomVideo = customSrc.match(/\.(mp4|webm|ogg|m4v)$/i);
        const mediaProps = {
          src: customSrc,
          alt: heading || 'Section media',
          style: {
            width: '100%',
            height: 'auto',
            maxHeight: '500px',
            objectFit: 'contain',
            borderRadius: '8px',
          },
        };
        return isCustomVideo ? <video {...mediaProps} autoPlay loop muted playsInline /> : <img {...mediaProps} />;
      }
    }

    // For image-based sections: show carousel if multiple images (includes default + customs stacked)
    if (!isComponentSection && resolvedAssets.length > 1) {
      return <ProposalCarousel images={resolvedAssets} heading={heading} />;
    }

    // Fallback: single image or video
    if (!assetSrc) {
      return null;
    }
    const mediaProps = {
      src: assetSrc,
      alt: heading || 'Section media',
      style: {
        width: '100%',
        height: 'auto',
        maxHeight: '500px',
        objectFit: 'contain',
        borderRadius: '8px',
      },
    };

    return isVideo ? <video {...mediaProps} autoPlay loop muted playsInline /> : <img {...mediaProps} />;
  }, [
    resolvedAssets,
    sectionData.imageUrls,
    isComponentSection,
    isInterfaceAndLed,
    isPhotos,
    isGreenScreen,
    isBackdropSection,
    isMasks,
    isGifs,
    isGlam,
    isPoseTips,
    isShareGeneral,
    isEmail,
    isSMS,
    isLiveGallery,
    backdrop,
    event,
    assetSrc,
    isVideo,
    heading,
    hasPhotoTemplates,
    greenScreenOverlayIds,
    sectionId,
  ]);

  const pricingContent = useMemo(() => {
    if (!isPricing) {
      return null;
    }

    const currencySymbol = sectionData.currency === 'EUR' ? '€' : '$';
    const sortedItems = sectionData.items ? [...sectionData.items].sort((a, b) => (a.order || 0) - (b.order || 0)) : [];

    return (
      <>
        {sortedItems.length > 0 && (
          <ul className="event-proposal__pricing-items">
            {sortedItems.map((item) => (
              <li key={item.id} className="event-proposal__pricing-item">
                <span className="event-proposal__pricing-item-bullet">•</span>
                {item.text}
              </li>
            ))}
          </ul>
        )}

        {sectionData.packagePricing && (
          <>
            <div className="event-proposal__pricing-label">Package Price</div>
            <div className="event-proposal__pricing-amount">
              {currencySymbol} {sectionData.packagePricing}
            </div>
          </>
        )}
      </>
    );
  }, [isPricing, sectionData]);

  // CTA button rendering (from brandingSettings.ctaSection via proposalConfig.button)
  const ctaButtonContent = useMemo(() => {
    if (!isCallToAction) {
      return null;
    }

    const buttonConfig = event?.proposalConfig?.button;
    if (!buttonConfig?.enabled || !buttonConfig?.text || !buttonConfig?.url) {
      return null;
    }

    const href = inputValidators.isEmail.validate(buttonConfig.url) ? `mailto:${buttonConfig.url}` : buttonConfig.url;

    return (
      <Button color={event.micrositeAccentColor} href={href} target="_blank">
        {buttonConfig.text}
      </Button>
    );
  }, [isCallToAction, event]);

  return (
    <section
      ref={ref}
      className={`event-proposal__page event-proposal__page--${sectionId}`}
      data-section={getSectionDataAttribute(sectionId)}
    >
      <div className="wrapper">
        <div className={`event-proposal__cols ${isCallToAction ? 'event-proposal__cols--vertical' : ''}`}>
          <div className="event-proposal__content">
            {heading && <h2 className="event-proposal__heading">{heading}</h2>}
            {body && <p className="event-proposal__text">{body}</p>}
            {pricingContent}
            {ctaButtonContent}
          </div>
          {previewContent && <div className="event-proposal__preview">{previewContent}</div>}
        </div>

        {isTemplatesOverlays && (
          <TemplatesOverlaysDisplay
            templates={sectionData.printTemplates || []}
            overlays={sectionData.overlays || []}
          />
        )}
      </div>
    </section>
  );
});

export default ProposalSection;
